import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import RichTextEditor from '@/components/RichTextEditor';
import RichTextDisplay from '@/components/RichTextDisplay';

function DefaultCard({ record, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(record.content || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(record.content || '');
  }, [record.content, editing]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/proposal-defaults/${record.key}`, { content: draft });
      onSaved(r.data);
      setEditing(false);
      toast.success(`${record.label} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(record.content || '');
    setEditing(false);
  };

  const dateText = record.updated_at ? new Date(record.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
  const byName = record.updated_by_name || '';

  return (
    <Card className="border-border bg-card shadow-none" data-testid={`default-card-${record.key}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium">{record.label}</div>
            {record.is_restricted && (
              <span
                className="inline-flex items-center gap-1 text-[10px] text-amber-500 border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 rounded-[4px]"
                title="Legal or financial content: handle with care"
                data-testid={`default-restricted-${record.key}`}
              >
                <Lock className="h-2.5 w-2.5" /> Restricted
              </span>
            )}
          </div>
          {canEdit && !editing && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8 border-border"
              onClick={() => setEditing(true)}
              data-testid={`default-edit-btn-${record.key}`}
            >
              <Pencil className="h-3 w-3 mr-1" /> Edit
            </Button>
          )}
        </div>

        {editing ? (
          <div className="space-y-3" data-testid={`default-editor-${record.key}`}>
            <RichTextEditor value={draft} onChange={setDraft} />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-8 border-border"
                onClick={cancel}
                data-testid={`default-cancel-btn-${record.key}`}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="text-xs h-8 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={save}
                disabled={saving}
                data-testid={`default-save-btn-${record.key}`}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <RichTextDisplay
            value={record.content}
            collapsedHeight={120}
            data-testid={`default-content-${record.key}`}
          />
        )}

        {(byName || dateText) && !editing && (
          <div className="text-[10px] text-muted-foreground">
            {byName ? `Last updated by ${byName}` : 'Not yet edited'}{dateText ? ` on ${dateText}` : ''}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ProposalDefaultsTab({ canEdit }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/proposal-defaults').then(r => setRecords(r.data)).catch(() => toast.error('Failed to load defaults')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSaved = (updated) => {
    setRecords(prev => prev.map(r => r.key === updated.key ? { ...r, ...updated } : r));
  };

  if (loading) {
    return <Card className="border-border bg-card shadow-none"><CardContent className="p-8 text-center text-xs text-muted-foreground">Loading...</CardContent></Card>;
  }

  return (
    <div className="space-y-3" data-testid="defaults-list">
      {records.map(r => (
        <DefaultCard key={r.key} record={r} canEdit={canEdit} onSaved={handleSaved} />
      ))}
    </div>
  );
}
